/**
 * Dynamic bloom crosshair — a 4-line reticle whose gap grows with the weapon's
 * current spread cone (CS/Valorant style). It is purely a readout of the cone
 * the server is also using; it never decides the bullet. Builds its own DOM so
 * it stays self-contained.
 */
export class CrosshairController {
  private readonly root: HTMLDivElement;
  private readonly lines: HTMLDivElement[] = [];
  private readonly dot: HTMLDivElement;

  constructor() {
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed',
      left: '50%',
      top: '50%',
      zIndex: '10',
      pointerEvents: 'none',
      display: 'none',
    } as CSSStyleDeclaration);

    const mk = (): HTMLDivElement => {
      const d = document.createElement('div');
      Object.assign(d.style, {
        position: 'absolute',
        background: 'rgba(255,255,255,0.85)',
        boxShadow: '0 0 1px rgba(0,0,0,0.9)',
      } as CSSStyleDeclaration);
      this.root.appendChild(d);
      this.lines.push(d);
      return d;
    };
    for (let i = 0; i < 4; i++) mk(); // top, bottom, left, right

    this.dot = document.createElement('div');
    Object.assign(this.dot.style, {
      position: 'absolute',
      width: '2px',
      height: '2px',
      left: '-1px',
      top: '-1px',
      background: 'rgba(255,255,255,0.9)',
    } as CSSStyleDeclaration);
    this.root.appendChild(this.dot);

    document.body.appendChild(this.root);
  }

  setVisible(on: boolean): void {
    this.root.style.display = on ? 'block' : 'none';
  }

  /** Size the reticle from the spread cone half-angle (radians). */
  update(spreadAngleRad: number, fovRad: number, screenHeightPx: number): void {
    // Project the cone half-angle to a screen radius (pixels).
    const px = ((screenHeightPx / 2) * Math.tan(spreadAngleRad)) / Math.tan(fovRad / 2);
    const gap = Math.max(3, Math.min(80, px));
    const len = 7;
    const thick = 2;
    const [top, bottom, left, right] = this.lines as [
      HTMLDivElement,
      HTMLDivElement,
      HTMLDivElement,
      HTMLDivElement,
    ];
    // Vertical lines (top/bottom)
    for (const [el, sign] of [
      [top, -1],
      [bottom, 1],
    ] as [HTMLDivElement, number][]) {
      el.style.width = `${thick}px`;
      el.style.height = `${len}px`;
      el.style.left = `${-thick / 2}px`;
      el.style.top = `${sign > 0 ? gap : -gap - len}px`;
    }
    // Horizontal lines (left/right)
    for (const [el, sign] of [
      [left, -1],
      [right, 1],
    ] as [HTMLDivElement, number][]) {
      el.style.height = `${thick}px`;
      el.style.width = `${len}px`;
      el.style.top = `${-thick / 2}px`;
      el.style.left = `${sign > 0 ? gap : -gap - len}px`;
    }
  }

  dispose(): void {
    this.root.remove();
  }
}
